import React, { useState, useEffect } from 'react';
import { 
  Card, 
  Input, 
  Button, 
  Table, 
  Space, 
  Typography, 
  Tag,
  Modal,
  Form,
  Badge,
  Avatar,
  Select,
  Spin,
  Tabs,
  Row,
  Col,
  Tooltip,
  App,
  Image
} from 'antd';
import { 
  UserAddOutlined,
  MailOutlined,
  PhoneOutlined,
  EyeOutlined,
  EditOutlined,
  UserOutlined,
  SettingOutlined,
  InfoCircleOutlined,
  BuildOutlined,
  ShareAltOutlined,
  CopyOutlined,
  ClockCircleOutlined,
  DeleteOutlined,
  ReloadOutlined,
  CrownOutlined,
  UploadOutlined
} from '@ant-design/icons';
import { useParams, useNavigate } from 'react-router-dom';
import businessUnitService from '../services/businessUnitService';
import apiClient from '../services/apiClient';
import PageHeader from './PageHeader';
import SearchCard from './SearchCard';
import { BREADCRUMB_PATTERNS } from './BreadcrumbNav';
import CSVUpload from './CSVUpload';
import Hidden from './Hidden';
import { PRIVILEGES } from '../constants/privileges';
import { countryCodes } from './countryCodes';

const { Title } = Typography;
const { Search } = Input;
const { Option } = Select;

const ParticipantManagement = () => {
  const { modal, message } = App.useApp();
  const { id: businessUnitId } = useParams();
  const navigate = useNavigate();
  const [businessUnit, setBusinessUnit] = useState(null);
  const [searchText, setSearchText] = useState('');
  const [loading, setLoading] = useState(true);
  const [participants, setParticipants] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 100,
    total: 0,
    pages: 0
  });
  const [invitationModalVisible, setInvitationModalVisible] = useState(false);
  const [invitationLoading, setInvitationLoading] = useState(false);
  const [selectedDepartment, setSelectedDepartment] = useState(null);
  const [invitationForm] = Form.useForm();
  
  // Manual participant entry states (kept for compatibility)
  const [addSelectedCountryCode, setAddSelectedCountryCode] = useState('+27');
  const [addPhoneCheckLoading, setAddPhoneCheckLoading] = useState(false);
  const [addPhoneError, setAddPhoneError] = useState(null);

  // Assign manager modal states
  const [assignManagerModalVisible, setAssignManagerModalVisible] = useState(false);
  const [selectedParticipantForManager, setSelectedParticipantForManager] = useState(null);
  const [assignManagerLoading, setAssignManagerLoading] = useState(false);

  // CSV upload modal states
  const [csvUploadModalVisible, setCsvUploadModalVisible] = useState(false);

  // Load business unit data and participants
  const loadBusinessUnit = async () => {
    try {
      setLoading(true);
      console.log('Loading business unit with ID:', businessUnitId);
      const result = await businessUnitService.getBusinessUnitById(businessUnitId);
      console.log('Business unit API result:', result);
      
      if (result.success) {
        console.log('Business unit data:', result.data);
        const businessUnitData = result.data.data || result.data;
        setBusinessUnit(businessUnitData);
        
        // Log invitation data specifically
        if (businessUnitData.invitation) {
          console.log('Business unit invitation data:', businessUnitData.invitation);
        } else {
          console.log('No invitation data found for business unit');
        }
        
        // Load participants for this business unit
        await loadParticipants();
      } else {
        console.error('Business unit API failed:', result.message);
        message.error(`Failed to load business unit: ${result.message}`);
        // Temporarily comment out the redirect to see the error
        // navigate('/dashboard/business-units');
      }
    } catch (error) {
      console.error('Load business unit error:', error);
      message.error(`Failed to load business unit: ${error.message}`);
      // Temporarily comment out the redirect to see the error
      // navigate('/dashboard/business-units');
    } finally {
      setLoading(false);
    }
  };

  // Load all participants for this business unit with search
  const loadParticipants = async (searchQuery = '', page = 1, pageSize = pagination.limit) => {
    try {
      console.log('Loading participants for business unit:', businessUnitId, 'search:', searchQuery);
      const options = {
        page,
        limit: pageSize
      };
      
      if (searchQuery.trim()) {
        options.search = searchQuery.trim();
      }
      
      const result = await businessUnitService.getParticipantsByBusinessUnit(businessUnitId, options);
      console.log('Participants API result:', result);
      
      if (result.success) {
        const responseData = result.data.data || result.data;
        const paginationData = result.data.pagination || {};
        
        setParticipants(responseData.data || responseData || []);
        setPagination({
          page: paginationData.page || 1,
          limit: paginationData.limit || 100,
          total: paginationData.total || 0,
          pages: paginationData.pages || 0
        });
      } else {
        console.error('Participants API failed:', result.message);
        message.error(`Failed to load participants: ${result.message}`);
        setParticipants([]);
      }
    } catch (error) {
      console.error('Load participants error:', error);
      message.error(`Failed to load participants: ${error.message}`);
      setParticipants([]);
    }
  };

  // Handle business unit edit
  const handleEditBusinessUnit = () => {
    if (businessUnit && businessUnit._id) {
      console.log('ParticipantManagement: Navigating to edit business unit page for:', businessUnit._id);
      navigate(`/dashboard/business-units/${businessUnit._id}?mode=edit`);
    } else {
      message.error('Business unit information not available');
    }
  };

  // Load business unit on component mount
  useEffect(() => {
    if (businessUnitId) {
      loadBusinessUnit();
    }
  }, [businessUnitId]);

  // Debounced search effect - only search if 3+ characters or empty (to show all)
  useEffect(() => {
    const searchTimeout = setTimeout(() => {
      // Only search if searchText is empty (show all) or has 3+ characters
      if (searchText.length === 0 || searchText.length >= 3) {
        loadParticipants(searchText, 1);
      }
    }, 500); // 500ms delay for search

    return () => clearTimeout(searchTimeout);
  }, [searchText]);

  // Handle search input change
  const handleSearchChange = (e) => {
    setSearchText(e.target.value);
  };

  // Handle Enter key press in search
  const handleSearchKeyPress = (e) => {
    if (e.key === 'Enter') {
      // Force search regardless of character count when Enter is pressed
      loadParticipants(searchText, 1);
    }
  };

  // Handle search button click (immediate search)
  const handleSearchClick = () => {
    loadParticipants(searchText, 1);
  };

  // Handle invitation submission
  const handleInvitationSubmit = async (values) => {
    setInvitationLoading(true);
    try {
      const invitationData = {
        email: values.email,
        businessUnitId: businessUnitId // Use the businessUnitId from URL params
      };

      // Use apiClient with manual message handling
      const response = await apiClient.post('/users/participants/invite', invitationData);
      
      if (response.data.success) {
        message.success('Invitation sent successfully! The participant will receive an email with registration instructions.');
        setInvitationModalVisible(false);
        invitationForm.resetFields();
      } else {
        message.error(response.data.message || 'Failed to send invitation');
      }
      
    } catch (error) {
      console.error('Error sending invitation:', error);
      message.error(error.response?.data?.message || 'Failed to send invitation. Please try again.');
    } finally {
      setInvitationLoading(false);
    }
  };

  // Invitation management functions
  const copyInvitationLink = async () => {
    if (!businessUnit?.invitation?.token) {
      message.error('No invitation link available');
      return;
    }

    const invitationLink = `${window.location.origin}/register/participant/${businessUnit.invitation.token}`;
    
    try {
      await navigator.clipboard.writeText(invitationLink);
      message.success('Invitation link copied to clipboard!');
    } catch (error) {
      // Fallback for browsers that don't support clipboard API
      const textArea = document.createElement('textarea');
      textArea.value = invitationLink;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      message.success('Invitation link copied to clipboard!');
    }
  };

  const handleRegenerateInvitation = async () => {
    modal.confirm({
      title: 'Recreate Invitation Link',
      content: 'This will generate a new invitation link with 7 days expiration. The old link will no longer work. Continue?',
      okText: 'Yes, Create New Link',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          const result = await businessUnitService.resendParticipantInvitation(businessUnitId);
          if (result.success) {
            message.success('New invitation link created successfully!');
            loadBusinessUnit(); // Refresh to get updated invitation data
          } else {
            message.error(result.message || 'Failed to create new invitation link');
          }
        } catch (error) {
          console.error('Error regenerating invitation:', error);
          message.error('Failed to create new invitation link');
        }
      }
    });
  };

  const handleCancelInvitation = async () => {
    modal.confirm({
      title: 'Cancel Invitation Link',
      content: 'This will disable the current invitation link. Participants will no longer be able to use it to register. Continue?',
      okText: 'Yes, Cancel Link',
      cancelText: 'Keep Link',
      okType: 'danger',
      onOk: async () => {
        try {
          const result = await businessUnitService.cancelParticipantInvitation(businessUnitId);
          if (result.success) {
            message.success('Invitation link cancelled successfully');
            loadBusinessUnit(); // Refresh to get updated data
          } else {
            message.error(result.message || 'Failed to cancel invitation link');
          }
        } catch (error) {
          console.error('Error canceling invitation:', error);
          message.error('Failed to cancel invitation link');
        }
      }
    });
  };

  const handleGenerateInvitation = async () => {
    modal.confirm({
      title: 'Generate Invitation Link',
      content: 'This will create a shareable invitation link for participants to join this business unit. Continue?',
      okText: 'Yes, Generate Link',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          const result = await businessUnitService.sendParticipantInvitation(businessUnitId, 'invite@taplearngo.com');
          if (result.success) {
            message.success('Invitation link generated successfully!');
            loadBusinessUnit(); // Refresh to get updated invitation data
          } else {
            message.error(result.message || 'Failed to generate invitation link');
          }
        } catch (error) {
          console.error('Error generating invitation:', error);
          message.error('Failed to generate invitation link');
        }
      }
    });
  };

  // Handle assign manager
  const handleAssignManager = (participant) => {
    setSelectedParticipantForManager(participant);
    setAssignManagerModalVisible(true);
  };

  // Handle manager assignment submission
  const handleManagerAssignmentSubmit = async () => {
    if (!selectedParticipantForManager || !businessUnit) {
      message.error('Missing required information');
      return;
    }

    setAssignManagerLoading(true);
    try {
      // Create the manager group name
      const managerGroupName = `${businessUnit.name} - Manager`;
      
      // Call API to assign user as business unit manager
      const result = await businessUnitService.assignBusinessUnitManager(
        businessUnitId,
        selectedParticipantForManager._id,
        managerGroupName
      );

      if (result.success) {
        message.success(`${selectedParticipantForManager.name} ${selectedParticipantForManager.surname} has been assigned as manager and will receive setup instructions via email.`);
        setAssignManagerModalVisible(false);
        setSelectedParticipantForManager(null);
        // Reload participants to reflect changes
        await loadParticipants(searchText, pagination.page);
      } else {
        message.error(result.message || 'Failed to assign manager');
      }
    } catch (error) {
      console.error('Assign manager error:', error);
      message.error('Failed to assign manager');
    } finally {
      setAssignManagerLoading(false);
    }
  };

  const formatExpirationDate = (expiresAt) => {
    if (!expiresAt) return 'No expiration';
    
    const now = new Date();
    const expiry = new Date(expiresAt);
    const diffTime = expiry - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffTime < 0) {
      return 'Expired';
    } else if (diffDays === 1) {
      return 'Expires today';
    } else if (diffDays <= 3) {
      return `Expires in ${diffDays} days`;
    } else {
      return `Expires ${expiry.toLocaleDateString()}`;
    }
  };

  const isInvitationExpired = (expiresAt) => {
    if (!expiresAt) return false;
    return new Date() > new Date(expiresAt);
  };

  return (
    <div style={{ padding: '24px' }}>
      {/* Header */}
      <div style={{ marginBottom: '24px' }}>
        <PageHeader 
          title={`${businessUnit?.name || 'Loading...'} - Team Management`}
          subtitle="Manage your organization's business units and departments"
          breadcrumbs={businessUnit?.name ? BREADCRUMB_PATTERNS.teamManagement(businessUnit.name) : BREADCRUMB_PATTERNS.businessUnits}
          breadcrumbClickHandlers={businessUnit?.name ? [
            () => navigate('/dashboard'), // Dashboard
            () => navigate('/dashboard/business-units'), // Business Units
            () => navigate('/dashboard/business-units'), // Business Unit Name (go back to list for now)
            null // Current page - Team Management
          ] : [
            () => navigate('/dashboard'), // Dashboard
            null // Business Units (current)
          ]}
          rightContent={
            businessUnit && (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px'
              }}>
                <div style={{
                  width: '80px',
                  height: '80px',
                  borderRadius: '8px',
                  background: businessUnit.backgroundColor ,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                
                }}>
                  {businessUnit.logo ? (
                    <Image
                    preview={false}
                      src={businessUnit.logo} 
                      alt={`${businessUnit.name} Logo`}
                      style={{
                      height: '80px',
                      width: '80px',
                        objectFit: 'contain'
                      }}
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                      }}
                    />
                  ) : (
                    <Image
                    preview={false} 
                      src="/logo.png" 
                      alt="Default Logo"
                      style={{
                        width: '80px',
                        height: '80px',
                        objectFit: 'contain'
                      }}
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                      }}
                    />
                  )}
                  <div style={{
                    display: 'none',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '40px',
                    height: '40px',
                    fontSize: '16px',
                    fontWeight: 'bold',
                    color: businessUnit.textColor || '#ffffff'
                  }}>
                    {businessUnit.name ? businessUnit.name.charAt(0).toUpperCase() : 'O'}
                  </div>
                </div>
              </div>
            )
          }
        />
      </div>

      <SearchCard
        searchPlaceholder="Search participants by name, email, department, or phone (min 3 chars)..."
        searchValue={searchText}
        onSearchChange={handleSearchChange}
        onSearchClick={handleSearchClick}
        onKeyPress={handleSearchKeyPress}
        minSearchLength={3}
        actionButtons={[
          {
            text: "Edit Business Unit",
            icon: <SettingOutlined />,
            onClick: handleEditBusinessUnit
          },
          {
            text: "Send Invitation",
           
            icon: <UserAddOutlined />,
            onClick: () => {
              setSelectedDepartment(null);
              setInvitationModalVisible(true);
            }
          },
          {
            text: "Add Participant",
            icon: <UserOutlined />,
            onClick: () => {
              navigate(`/dashboard/businessunit/${businessUnitId}/member/new?mode=create`);
            }
          },
          {
            text: "Upload CSV",
            icon: <UploadOutlined />,
            onClick: () => setCsvUploadModalVisible(true)
          },
          {
            text: "Refresh",
            onClick: () => loadParticipants(searchText, 1)
          }
        ]}
      />

      {/* Invitation Management Section - Only show if there's an existing invitation */}
      {businessUnit?.invitation && businessUnit.invitation.token && (
        <Card style={{ marginBottom: '24px', borderLeft: '4px solid #52c41a' }}>
          <div style={{ marginBottom: '12px' }}>
            <Title level={5} style={{ margin: 0 }}>
              Invitation Link Management
            </Title>
          </div>
          
          {!isInvitationExpired(businessUnit.invitation.expiresAt) ? (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: '13px', color: '#666' }}>
                Link expires {formatExpirationDate(businessUnit.invitation.expiresAt)}
              </span>
              <Space size="small">
                <Button 
                  size="small"
                  icon={<CopyOutlined />}
                  onClick={copyInvitationLink}
                >
                  Copy
                </Button>
                <Button 
                  size="small"
                  icon={<ReloadOutlined />}
                  onClick={handleRegenerateInvitation}
                >
                  Recreate
                </Button>
                <Button 
                  size="small"
                  danger
                  icon={<DeleteOutlined />}
                  onClick={handleCancelInvitation}
                >
                  Cancel
                </Button>
              </Space>
            </div>
          ) : (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: '13px', color: '#999' }}>
                Invitation link expired
              </span>
              <Button 
                size="small"
                type="default"
                icon={<ReloadOutlined />}
                onClick={handleRegenerateInvitation}
              >
                Create New Link
              </Button>
            </div>
          )}
        </Card>
      )}

      {/* Participants List */}
      <Card>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <Title level={4} style={{ margin: 0 }}>
            Users ({pagination.total})
            {searchText && <span style={{ color: '#666', fontWeight: 'normal' }}> - Search: "{searchText}"</span>}
          </Title>
        </div>

        <Table
          dataSource={participants.map((participant) => ({
            key: participant._id,
            ...participant
          }))}
          columns={[
            {
              title: 'Name',
              key: 'name',
              render: (_, participant) => (
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <Avatar 
                    size={30} 
                    src={participant.profilePicture} 
                    style={{ marginRight: 12, backgroundColor: '#2E86AB' }}
                  >
                    {participant.name?.charAt(0)?.toUpperCase() || 'U'}
                  </Avatar>
                  <div>
                    <div style={{ fontWeight: 500 }}>
                      {participant.name} {participant.surname}
                    </div>
                    <div style={{ color: '#666', fontSize: '12px' }}>
                      {participant.email}
                    </div>
                  </div>
                </div>
              )
            },
            {
              title: 'Department',
              dataIndex: 'department',
              key: 'department',
              render: (department) => (
                <Tag color="blue">{department || 'Unassigned'}</Tag>
              )
            },
            {
              title: 'Phone',
              key: 'phone',
              render: (_, participant) => (
                participant.phoneNumber?.full || 'Not provided'
              )
            },
            {
              title: 'Status',
              key: 'status',
              render: (_, participant) => (
                <Badge 
                  status={participant.status === "active" ? 'success' : 'default'} 
                  text={participant.status === "active" ? 'Active' : 'Inactive'} 
                />
              )
            },
            {
              title: 'Action',
              key: 'action',
              render: (_, participant) => (
                <Space size="small">
                  <Tooltip title="View Details">
                    <Button
                      type="text"
                      size="small"
                      icon={<EyeOutlined />}
                      onClick={() => navigate(`/dashboard/member/${participant._id}`)}
                    />
                  </Tooltip>
                  <Tooltip title="Edit">
                    <Button
                      type="text"
                      size="small"
                      icon={<EditOutlined />}
                      onClick={() => navigate(`/dashboard/businessunit/${businessUnitId}/member/${participant._id}/edit`)}
                    />
                  </Tooltip>
                  <Hidden privilege={PRIVILEGES.SYSTEM_ADMIN}>
                    <Tooltip title="Assign Manager">
                      <Button
                        type="text"
                        size="small"
                        icon={<CrownOutlined />}
                        onClick={() => handleAssignManager(participant)}
                        style={{ color: '#faad14' }}
                      />
                    </Tooltip>
                  </Hidden>
                </Space>
              )
            }
          ]}
          pagination={{
            current: pagination.page,
            pageSize: pagination.limit,
            total: pagination.total,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
            onChange: (page, pageSize) => {
              // When page size changes, reset to page 1 so all items are visible
              const sizeChanged = pageSize !== pagination.limit;
              const targetPage = sizeChanged ? 1 : page;
              setPagination(prev => ({ ...prev, limit: pageSize, page: targetPage }));
              loadParticipants(searchText, targetPage, pageSize);
            },
            pageSizeOptions: ['20', '50', '100', '200']
          }}
          loading={loading}
        />
      </Card>

      {/* Invitation Modal */}
      <Modal
        title="Send Participant Invitation"
        open={invitationModalVisible}
        onCancel={() => {
          if (!invitationLoading) {
            setInvitationModalVisible(false);
            invitationForm.resetFields();
          }
        }}
        closable={!invitationLoading}
        maskClosable={!invitationLoading}
        footer={null}
        width={500}
      >
        <Form
          form={invitationForm}
          layout="vertical"
          onFinish={handleInvitationSubmit}
        >
          <Form.Item
            name="email"
            label="Email Address"
            rules={[
              { required: true, message: 'Please enter email address' },
              { type: 'email', message: 'Please enter a valid email address' }
            ]}
          >
            <Input
              prefix={<MailOutlined />}
              placeholder="participant@example.com"
            />
          </Form.Item>

          <div style={{ 
            backgroundColor: '#f0f8ff', 
            border: '1px solid #d6ebff', 
            borderRadius: '6px', 
            padding: '12px', 
            marginBottom: '24px' 
          }}>
            <div style={{ display: 'flex', alignItems: 'flex-start' }}>
              <div style={{ color: '#1890ff', marginRight: '8px', marginTop: '2px' }}>
                <svg width="16" height="16" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                </svg>
              </div>
              <div style={{ fontSize: '14px', color: '#1890ff' }}>
                The participant will receive an email with registration instructions and can choose their department during registration.
              </div>
            </div>
          </div>

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button 
                onClick={() => {
                  if (!invitationLoading) {
                    setInvitationModalVisible(false);
                    invitationForm.resetFields();
                  }
                }}
                disabled={invitationLoading}
              >
                Cancel
              </Button>
              <Button 
              
                htmlType="submit"
                loading={invitationLoading}
                disabled={invitationLoading}
              >
                {invitationLoading ? 'Sending...' : 'Send Invitation'}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>


      {/* Assign Manager Modal */}
      <Modal
        title="Assign Business Unit Manager"
        open={assignManagerModalVisible}
        onOk={handleManagerAssignmentSubmit}
        onCancel={() => {
          setAssignManagerModalVisible(false);
          setSelectedParticipantForManager(null);
        }}
        okText="Assign as Manager"
        cancelText="Cancel"
        confirmLoading={assignManagerLoading}
        okButtonProps={{ 
          icon: <CrownOutlined />,
          style: { backgroundColor: '#faad14', borderColor: '#faad14', color: '#fff' }
        }}
      >
        {selectedParticipantForManager && (
          <div>
            <p>You are about to assign <strong>{selectedParticipantForManager.name} {selectedParticipantForManager.surname}</strong> as the manager for:</p>
            <div style={{ 
              background: '#f6ffed', 
              border: '1px solid #b7eb8f', 
              borderRadius: '6px', 
              padding: '12px',
              margin: '16px 0'
            }}>
              <h4 style={{ margin: '0 0 8px 0', color: '#2E86AB' }}>{businessUnit?.name}</h4>
              <p style={{ margin: 0, fontSize: '14px', color: '#666' }}>
                Manager Group: "{businessUnit?.name} - Manager"
              </p>
            </div>
            <div style={{ 
              background: '#fff7e6', 
              border: '1px solid #ffd591', 
              borderRadius: '6px', 
              padding: '12px' 
            }}>
              <h5 style={{ margin: '0 0 8px 0', color: '#d48806' }}>What happens next:</h5>
              <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '14px' }}>
                <li>A manager group will be created automatically</li>
                <li>The user will receive an email to set up their manager password</li>
                <li>They will gain full management access to this business unit</li>
                <li>They can invite participants, manage departments, and generate invitation links</li>
              </ul>
            </div>
          </div>
        )}
      </Modal>

      {/* CSV Upload Modal */}
      <Modal
        title="Upload Participants CSV"
        open={csvUploadModalVisible}
        onCancel={() => setCsvUploadModalVisible(false)}
        footer={null}
        width={800}
      >
        <CSVUpload
          businessUnitId={businessUnitId}
          businessUnitName={businessUnit?.name}
          onSuccess={() => {
            setCsvUploadModalVisible(false);
            loadParticipants(searchText, 1); // Refresh participants list
          }}
        />
      </Modal>
    </div>
  );
};

export default ParticipantManagement;
